import { dealPlankListLabelID } from '@/util/dealPaibanData.js'
import { isTextureAlignmentPlank } from '@/util/plankAlignTexture'
import { dealMaterialList } from '@/util/plankCommonFuncs'

import type { IFilterableFieldsType } from '../types/material'
import type { PartType, PlankType } from '../types/part'
import type { SpecialRemarksType } from './show'
import {
  processLabelFn,
  showMaterialHighGloss,
  showMaterialHingeinfo,
} from './show'

/**
 * @param plankList 原始料单展示数据
 * @param filterObj 筛选条件
 * @param searchCondition 搜索条件（尺寸，板件名称，板号）
 * @param specialRemarks 可选，特殊工艺筛选配置，用于 process_label 展示与筛选一致
 */
export const dealFilterData = (
  plankList: Readonly<PlankType[]>,
  filterObj?: Partial<Record<IFilterableFieldsType, any[]>>,
  searchCondition?: {
    searchKey: string
    minSize: number | string
    maxSize: number | string
    searchKeyType: string
  },
  specialRemarks?: SpecialRemarksType | null
) => {
  const filterPropOri = (key: keyof PartType, part: PartType) =>
    getFilterPropOriData(key, part, specialRemarks)
  const filterKeys =
    filterObj && (Object.keys(filterObj) as IFilterableFieldsType[])
  let newPlankList = plankList
  if (filterObj && filterKeys && filterKeys.length) {
    newPlankList = plankList
      .map((plank) => {
        const parts = plank.parts.filter((part) => {
          const listArr: boolean[] = []
          filterKeys.forEach((key) => {
            if (key && filterObj[key]?.includes(filterPropOri(key, part))) {
              listArr.push(true)
            } else {
              listArr.push(false)
            }
          })
          return listArr.every((item) => item)
        })
        return {
          ...plank,
          parts,
        }
      })
      .filter((plank) => plank.parts.length)
  }

  // 关键字搜索和尺寸搜索互斥
  if (searchCondition) {
    const { minSize, maxSize, searchKey } = searchCondition
    if (searchCondition.minSize && searchCondition.maxSize) {
      const newMinSize = Number(minSize)
      const newMaxSize = Number(maxSize)
      newPlankList = newPlankList
        .map((plank) => {
          return {
            ...plank,
            parts: plank.parts.filter((part) =>
              rangeSize(part, newMinSize, newMaxSize)
            ),
          }
        })
        .filter((plank) => plank.parts.length)
    }
    if (searchKey) {
      const newList: PlankType[] = []
      newPlankList.forEach((plank) => {
        const parts = plank.parts.filter((part) => {
          // const f =
          //   part.partName.includes(searchKey) ||
          //   part.plankID == Number(searchKey) // 此处用==，因为plankID变为了string类型
          let f = false
          if (searchCondition.searchKeyType === 'process_label') {
            const processLabel = processLabelFn(part, specialRemarks)
            f = processLabel.includes(searchKey)
          } else {
            // 全部改成字符串 因为某些字段（板号）是number类型
            f = String(
              part[searchCondition.searchKeyType as keyof PartType]
            ).includes(searchKey)
          }
          if (f) return true
          // else {
          //   // 这里有问题
          //   part.isSelected = true
          // }
        })
        if (parts.length) newList.push({ ...plank, parts })
      })
      newPlankList = newList
    }
  }

  const result = dealPlankListLabelID(
    dealMaterialList(newPlankList as PlankType[])
  )
  return result as PlankType[]
}

// 判断板件尺寸是否在搜索范围内
const rangeSize = (part: PartType, minSize: number, maxSize: number) => {
  return (
    (part.specHeight <= Number(maxSize) &&
      part.specHeight >= Number(minSize)) ||
    (part.specWidth <= Number(maxSize) && part.specWidth >= Number(minSize))
  )
}

/**
 * @description 孔槽数量筛选值：有孔槽 / 无孔槽
 * hsInfo 如「14孔0槽」「0孔10槽」「14孔12槽」为有孔槽，「0孔0槽」为无孔槽
 * @returns 'hasHsInfo' | 'noHsInfo'
 */
export function getHsInfoFilterValue(plank: PartType): 'hasHsInfo' | 'noHsInfo' {
  const hsInfo = String(plank.hsInfo ?? '').trim()
  const match = hsInfo.match(/(\d+)\s*孔\s*(\d+)\s*槽/)
  if (!match) {
    return 'noHsInfo'
  }
  const holeNum = Number(match[1])
  const slotNum = Number(match[2])
  return holeNum > 0 || slotNum > 0 ? 'hasHsInfo' : 'noHsInfo'
}

/**
 * @description 获取指定 prop 的筛选源对应的板件内部字段数据（显示时可能做了转换）
 * @param specialRemarks 可选，用于 process_label 时按特殊工艺筛选配置过滤
 */
export function getFilterPropOriData(
  prop: keyof PartType,
  plank: PartType,
  specialRemarks?: SpecialRemarksType | null
) {
  switch (prop) {
    case 'hingeInfo':
      return showMaterialHingeinfo(plank)
    case 'is_high_gloss_plank':
      return showMaterialHighGloss(plank)
    case 'process_label':
      return processLabelFn(plank, specialRemarks)
    case 'textureGrainInfo':
      return isTextureAlignmentPlank(plank)
        ? plank.textureGrainInfo?.groupName ?? ''
        : ''
    case 'hsInfo':
      return getHsInfoFilterValue(plank)
    default:
      return plank[prop]
  }
}
